import type { QueryClient } from "@tanstack/react-query"
import { friendsQueryOptions } from "../api/queries"
import type { Friend } from "../model/types"

/** Обновляет одного друга в кеше списка друзей по friendId */
export function updateFriendInCache(
	queryClient: QueryClient,
	userId: number,
	friendId: number,
	updater: (friend: Friend) => Friend,
) {
	const { queryKey } = friendsQueryOptions(userId)
	queryClient.setQueryData(queryKey, (old) =>
		old?.map((friend) => {
			if (friend.id === friendId) return updater(friend)
			return friend
		}),
	)
}

/**
 * Вставляет или заменяет друга в кеше списка друзей. Пустой кеш не трогаем:
 * setQueryData пометил бы одноэлементный список свежим (staleTime: Infinity),
 * а первый настоящий fetch и так вернёт полный список.
 */
export function upsertFriendInCache(
	queryClient: QueryClient,
	userId: number,
	friend: Friend,
) {
	const { queryKey } = friendsQueryOptions(userId)
	queryClient.setQueryData(queryKey, (old) => {
		if (!old) return old
		if (!old.some((f) => f.id === friend.id)) return [friend, ...old]
		return old.map((f) => {
			if (f.id === friend.id) return friend
			return f
		})
	})
}

/**
 * Убирает друга из кеша списка — после подтверждённого бэком удаления связи.
 */
export function removeFriendFromCache(
	queryClient: QueryClient,
	userId: number,
	friendId: number,
) {
	const { queryKey } = friendsQueryOptions(userId)
	queryClient.setQueryData(queryKey, (old) =>
		old?.filter((friend) => friend.id !== friendId),
	)
}

/**
 * Upsert друга, чья дружба уже подтверждена бэком.
 */
export function upsertConfirmedFriendInCache(
	queryClient: QueryClient,
	userId: number,
	friend: Friend,
) {
	upsertFriendInCache(queryClient, userId, {
		...friend,
		status: "friends",
	})
}
